/**
 * Grist Geoportail Map Widget
 *
 * A custom Grist widget that displays geographic data on an interactive
 * Leaflet map with French IGN/Geoportail integration.
 *
 * @module main
 */

import { render, h } from 'preact';
import { signal } from '@preact/signals';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet.markercluster';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
import 'geoportal-extensions-leaflet/dist/GpPluginLeaflet.css';
import 'geoportal-extensions-leaflet';

import { DEFAULT_MAP_CENTER, DEFAULT_MAP_ZOOM, OSM_TILE_URL, OSM_ATTRIBUTION } from './constants';
import { setup } from 'goober';
import './styles';
import { ColorManager } from './utils';
import {
  BoundaryService,
  initSentry,
  setContext,
  captureException,
  addBreadcrumb,
} from './services';
import {
  IconFactory,
  fixLeafletIconPaths,
  MarkerManager,
  IsochroneController,
  App,
} from './components';
import {
  GristBridge,
  MockRecordProvider,
  createSampleRecords,
  DEFAULT_SAMPLE_MAPPINGS,
} from './grist';
import type { MapConfig, GristRecord, IsoPoint, RecordProvider } from './types';

// Fix Leaflet default icon paths
fixLeafletIconPaths();

// Initialize Sentry for error tracking
const sentryInitialized = initSentry();
if (sentryInitialized) {
  setContext('widget', {
    name: 'grist-geoportail',
    version: '2.0.0',
  });
}

// Detect test mode from URL or environment
const isTestMode = (): boolean => {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get('test') === 'true';
};

// Create test provider with sample data
const createTestProvider = (): RecordProvider => {
  return new MockRecordProvider({
    records: createSampleRecords(5),
    mappings: DEFAULT_SAMPLE_MAPPINGS,
  });
};

/**
 * Main map widget class that orchestrates all components
 */
class MapWidget {
  private map: L.Map;
  private gristBridge: GristBridge;
  private colorManager: ColorManager;
  private iconFactory: IconFactory;
  private markerManager: MarkerManager;
  private isochroneController: IsochroneController;
  private boundaryService: BoundaryService;
  private selectedRecord: GristRecord | null = null;

  // Reactive signals for UI state
  private typesSignal = signal<Set<string>>(new Set());
  private regionsEnabled = signal<boolean>(false);
  private departmentsEnabled = signal<boolean>(false);

  constructor() {
    try {
      addBreadcrumb('MapWidget constructor started', 'lifecycle');
      // Initialize goober for Preact before any components mount
      setup(h);
      // Initialize map
      this.map = this.initMap();
      addBreadcrumb('Map initialized', 'lifecycle');

      // Initialize color manager with persistence callback
      this.colorManager = new ColorManager({}, (typeColorMap) => {
        this.gristBridge.saveTypeColors(typeColorMap);
      });

      // Initialize icon factory
      this.iconFactory = new IconFactory();

      // Initialize marker manager
      this.markerManager = new MarkerManager(this.map, this.colorManager, this.iconFactory, {
        onMarkerClick: (_record, marker) => {
          const point: IsoPoint = {
            lat: marker.getLatLng().lat,
            lng: marker.getLatLng().lng,
            name: String(_record[this.gristBridge.getConfig().nameColumn] || 'Sans nom'),
          };
          this.isochroneController.setPoint(point);
        },
        onSelectRecord: (recordId) => {
          this.gristBridge.selectRecord(recordId);
        },
      });

      // Initialize isochrone controller
      this.isochroneController = new IsochroneController(this.map);

      // Initialize boundary service
      this.boundaryService = new BoundaryService();
      this.boundaryService.setMap(this.map);

      // Render Preact UI
      this.renderUI();

      // Setup resize handler
      window.addEventListener('resize', () => {
        this.map.invalidateSize();
      });

      // Initialize Grist bridge with optional test provider
      const provider = isTestMode() ? createTestProvider() : undefined;
      this.gristBridge = new GristBridge(
        {
          onRecordsChange: (records, config) => this.handleRecordsChange(records, config),
          onRecordSelect: (record) => this.handleRecordSelect(record),
          onConfigLoaded: (config) => this.handleConfigLoaded(config),
        },
        provider
      );

      this.gristBridge.initialize();
      addBreadcrumb('MapWidget initialized successfully', 'lifecycle');
    } catch (error) {
      captureException(error as Error, {
        component: 'MapWidget',
        phase: 'initialization',
      });
      console.error('Failed to initialize MapWidget:', error);
      throw error;
    }
  }

  /**
   * Initialize the Leaflet map
   */
  private initMap(): L.Map {
    const map = L.map('map').setView(DEFAULT_MAP_CENTER, DEFAULT_MAP_ZOOM);

    L.tileLayer(OSM_TILE_URL, {
      attribution: OSM_ATTRIBUTION,
    }).addTo(map);

    return map;
  }

  /**
   * Render Preact UI components
   */
  private renderUI(): void {
    const uiContainer = document.getElementById('ui-root');
    if (!uiContainer) {
      // eslint-disable-next-line no-console
      console.error('UI root container not found');
      return;
    }

    render(
      <App
        types={this.typesSignal}
        colorManager={this.colorManager}
        onColorChange={(type, color) => this.markerManager.updateTypeColor(type, color)}
        regionsEnabled={this.regionsEnabled}
        departmentsEnabled={this.departmentsEnabled}
        onRegionToggle={(enabled) => this.handleRegionToggle(enabled)}
        onDepartmentToggle={(enabled) => this.handleDepartmentToggle(enabled)}
        isochroneController={this.isochroneController}
        onFilterChange={(checked) => this.handleFilterChange(checked)}
      />,
      uiContainer
    );
  }

  /**
   * Handle isochrone filter checkbox toggle
   */
  private handleFilterChange(checked: boolean): void {
    this.isochroneController.filterMarkers.value = checked;

    if (checked) {
      const geometry = this.isochroneController.getIsochroneGeometry();
      if (geometry) {
        this.markerManager.filterByPolygon(geometry);
      }
    } else {
      this.markerManager.showAllMarkers();
    }
  }

  /**
   * Handle region boundary toggle
   */
  private handleRegionToggle(enabled: boolean): void {
    this.regionsEnabled.value = enabled;
    addBreadcrumb(`Toggle regions: ${enabled}`, 'user-action');
    this.boundaryService.toggleRegions(enabled).catch((error) => {
      captureException(error as Error, { feature: 'regions', enabled });
      this.regionsEnabled.value = false;
    });
  }

  /**
   * Handle department boundary toggle
   */
  private handleDepartmentToggle(enabled: boolean): void {
    this.departmentsEnabled.value = enabled;
    addBreadcrumb(`Toggle departments: ${enabled}`, 'user-action');
    this.boundaryService.toggleDepartments(enabled).catch((error) => {
      captureException(error as Error, { feature: 'departments', enabled });
      this.departmentsEnabled.value = false;
    });
  }

  /**
   * Handle records change from Grist
   */
  private handleRecordsChange(records: GristRecord[], config: MapConfig): void {
    try {
      addBreadcrumb(`Records changed: ${records.length} records`, 'data');
      // Update markers
      this.markerManager.updateMarkers(records, config);

      // Update legend if type column is configured
      if (config.typeColumn) {
        const types = this.markerManager.getTypes();
        this.typesSignal.value = types;
      } else {
        this.typesSignal.value = new Set();
      }

      // Re-apply selection if needed
      if (this.selectedRecord) {
        this.markerManager.setSelectedRecord(this.selectedRecord.id);
      }
    } catch (error) {
      captureException(error as Error, {
        component: 'MapWidget',
        method: 'handleRecordsChange',
        recordCount: records.length,
      });
      console.error('Failed to handle records change:', error);
    }
  }

  /**
   * Handle record selection from Grist
   */
  private handleRecordSelect(record: GristRecord | null): void {
    this.selectedRecord = record;

    if (record) {
      this.markerManager.setSelectedRecord(record.id);
      this.markerManager.centerOnRecord(record, this.gristBridge.getConfig());
    } else {
      this.markerManager.setSelectedRecord(null);
    }
  }

  /**
   * Handle configuration loaded from Grist
   */
  private handleConfigLoaded(config: MapConfig): void {
    // Restore color mappings
    if (config.typeColorMap) {
      this.colorManager.setColorMap(config.typeColorMap);
    }
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  new MapWidget();
});
